// PurchaseOrderItemsSettingsModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Local
import {
  DEFAULT_PURCHASE_ORDER_ITEMS_DISPLAY_PREFS,
  normalizePurchaseOrderItemsDisplayPrefs,
} from 'nodetypes/purchaseorder/view/purchaseOrderItemsDisplayPrefs';

// CSS
import './PurchaseOrderItemsSettingsModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Switch from 'components/switch/Switch';

const ITEM_DETAIL_OPTIONS = [
  {
    key: 'showDescription',
    label: 'Show description',
  },
  {
    key: 'showCoverPhoto',
    label: 'Show cover photo',
  },
  {
    key: 'showLineNumber',
    label: 'Show line number',
  },
];

/**
 * PurchaseOrderItemsSettingsModal
 *
 * Customize Item Details display for the Purchase Order Items table.
 *
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {object} [prefs]
 * @param {function} [onSave]
 * @returns {ReactNode}
 */
const PurchaseOrderItemsSettingsModal = React.memo(({
  isOpen,
  onClose,
  prefs = DEFAULT_PURCHASE_ORDER_ITEMS_DISPLAY_PREFS,
  onSave = null,
}) => {
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  // Draft prefs edited in the modal before Save.
  const [draft, setDraft] = useState(() => normalizePurchaseOrderItemsDisplayPrefs(prefs));

  // ----- EFFECTS ----- //

  // Reset draft when the modal opens or saved prefs change.
  useEffect(() => {
    if (!isOpen) return;
    setDraft(normalizePurchaseOrderItemsDisplayPrefs(prefs));
  }, [isOpen, prefs]);

  // ----- HANDLERS ----- //

  /**
   * Updates one draft toggle.
   *
   * @param {string} key
   * @param {boolean} nextValue
   * @returns {void}
   */
  const handleToggle = (key, nextValue) => {
    setDraft((prev) => ({
      ...prev,
      [key]: Boolean(nextValue),
    }));
  };

  /**
   * Persists draft prefs and closes.
   *
   * @returns {void}
   */
  const handleSave = () => {
    if (typeof onSave === 'function') {
      onSave(normalizePurchaseOrderItemsDisplayPrefs(draft));
    }
    if (typeof onClose === 'function') onClose();
  };

  // ----- RENDER ----- //

  return (
    <Modal
      iconKey="discover_tune"
      title="Settings"
      isOpen={isOpen}
      width={isMobile ? '100%' : 420}
      height="auto"
      onClose={onClose}
    >
      <ModalForm>
        {/* ITEM DETAILS */}
        <div className="purchase-order-items-settings-modal-section">
          <div className="purchase-order-items-settings-modal-section-title">
            Item Details
          </div>

          <div className="purchase-order-items-settings-modal-options">
            {ITEM_DETAIL_OPTIONS.map((option) => (
              <div
                key={option.key}
                className="purchase-order-items-settings-modal-option-row"
              >
                <div className="purchase-order-items-settings-modal-option-label">
                  {option.label}
                </div>
                <Switch
                  checked={Boolean(draft[option.key])}
                  onChange={(nextValue) => handleToggle(option.key, nextValue)}
                />
              </div>
            ))}
          </div>
        </div>
      </ModalForm>

      <ModalButtons>
        <Button
          iconKey="check"
          label="Save"
          onClick={handleSave}
        />
      </ModalButtons>
    </Modal>
  );
});

PurchaseOrderItemsSettingsModal.displayName = 'PurchaseOrderItemsSettingsModal';

export default PurchaseOrderItemsSettingsModal;
